import React, { useRef, useState } from 'react';
import { Upload, Sparkles } from 'lucide-react';

const ACCEPT_STRING =
  '.pdf,.ai,.eps,.svg,.png,.jpg,.jpeg,.tif,.tiff,.psd,.webp,image/*,application/pdf,application/postscript,application/illustrator,image/vnd.adobe.photoshop,application/x-photoshop';

/**
 * MockupIdleDropzone — the artwork drop target that mounts INSIDE the
 * Live Mockup card's idle state so the customer drops their file onto
 * the same panel where the rendered preview will appear.
 *
 * iter114p — replaces the iter114k separate "Drop artwork here" panel
 * that sat below the mockup window. Customer asked to merge the two
 * panels into one ("incorporate both the drop art and rendering into
 * one"). The dropzone now occupies the entire mockup-window idle
 * state; once a file lands, the canvas mockup paints over the same
 * region. Single mental model = one "drop & see" interaction.
 *
 * Props:
 *   - onFile(file)  — required callback fired when a file is selected
 *     or dropped. Caller validates / uploads / sets state.
 *   - maxFileMb     — display-only file-size hint (default 200).
 *   - disabled      — (not currently used; reserved for future
 *     "anonymous + cannot upload" gating).
 */
export const MockupIdleDropzone = ({ onFile, maxFileMb = 200, disabled = false }) => {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    onFile?.(e.dataTransfer.files?.[0]);
  };

  return (
    <button
      type="button"
      onClick={() => !disabled && fileInputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      disabled={disabled}
      data-testid="mockup-idle-dropzone"
      className={`group absolute inset-0 flex flex-col items-center justify-center gap-3 transition-all text-center px-6 ${
        dragOver
          ? 'bg-[#6FBF73]/15'
          : 'bg-transparent hover:bg-[#6FBF73]/5'
      } ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      {/* Sparkle drift behind the upload icon — iter114s.69: emerald/amber
          radial wash flipped to soft EG3 green so the empty state reads
          as continuous with the new light-green panel chrome. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          background:
            'radial-gradient(circle at 30% 25%, rgba(111, 191, 115, 0.18), transparent 35%), '
            + 'radial-gradient(circle at 70% 75%, rgba(168, 216, 172, 0.20), transparent 40%)',
        }}
      />

      <div className={`relative flex items-center justify-center w-20 h-20 rounded-full ring-2 ring-[#6FBF73]/50 ${
        dragOver ? 'bg-[#6FBF73]/20 scale-110' : 'bg-white/70 group-hover:bg-[#6FBF73]/15 group-hover:scale-105'
      } transition-all`}>
        <Upload className={`w-9 h-9 ${dragOver ? 'text-[#2F5D3A]' : 'text-[#4A7C59]'} transition-transform group-hover:-rotate-6`} />
      </div>

      <div className="relative">
        <p className="text-base sm:text-lg font-bold text-[#14281D]">
          Drop artwork here or click to browse
        </p>
        <p className="text-[11px] sm:text-xs text-[#1F3D2C]/70 mt-1">
          PDF · AI · EPS · SVG · PNG · JPG · TIFF · PSD · up to {maxFileMb}MB
        </p>
        <p className="text-[11px] text-[#2F5D3A] mt-2 flex items-center justify-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Live mockup renders the moment your file uploads.</span>
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_STRING}
        onChange={(e) => onFile?.(e.target.files?.[0])}
        className="hidden"
        data-testid="mockup-idle-dropzone-input"
      />
    </button>
  );
};

export default MockupIdleDropzone;
